import { useCallback } from 'react';
import type { DataGroup } from '@/features/DataGrid';
import { useAppDispatch } from '@/shared/model';
import { getMinMax, getPage, getTotalData, getTreeData } from './slice';
import type { DataConfig } from './types';

export function useDataFetchers(config: DataConfig) {
  const dispatch = useAppDispatch();

  const onGetRows = useCallback(
    (offset?: number) => {
      return dispatch(
        getPage({
          dimensions: config.dimensions,
          metric: config.metric,
          dateScale: config.dateScale,
          dateRange: config.dateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          filters: config.filters,
          offset,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      config.metric,
      config.dateScale,
      config.dateRange,
      config.sorting,
      config.probabilisticAttribution,
      config.lifetime,
      config.filters,
    ],
  );

  const onGetTotal = useCallback(
    () => {
      return dispatch(
        getTotalData({
          dimensions: config.dimensions,
          metric: config.metric,
          dateRange: config.dateRange,
          dateScale: config.dateScale,
          lifetime: config.lifetime,
          filters: config.filters,
          probabilisticAttribution: config.probabilisticAttribution,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      config.metric,
      config.dateRange,
      config.dateScale,
      config.lifetime,
      config.filters,
      config.probabilisticAttribution,
    ],
  );

  const onGetTreeData = useCallback(
    (dataGroups: DataGroup[]) => {
      return dispatch(
        getTreeData({
          dimensions: config.dimensions,
          metric: config.metric,
          dateScale: config.dateScale,
          dateRange: config.dateRange,
          sorting: config.sorting,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          filters: config.filters,
          parentGroups: dataGroups,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dimensions,
      config.metric,
      config.dateScale,
      config.dateRange,
      config.lifetime,
      config.sorting,
      config.filters,
      config.probabilisticAttribution,
    ],
  );

  const onGetMinMax = useCallback(
    () => {
      return dispatch(
        getMinMax({
          dimensions: config.dimensions,
          metric: config.metric,
          filters: config.filters,
          dateRange: config.dateRange,
          dateScale: config.dateScale,
          probabilisticAttribution: config.probabilisticAttribution,
          lifetime: config.lifetime,
          dataGridViewType: config.dataGridViewType,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      dispatch,
      config.dataGridViewType,
      config.dimensions,
      config.metric,
      config.filters,
      config.dateRange,
      config.dateScale,
      config.probabilisticAttribution,
      config.lifetime,
    ],
  );

  return { onGetRows, onGetTotal, onGetTreeData, onGetMinMax };
}
